import _ from "lodash";
// third party libraries
import update from "immutability-helper";
import client from "lib/apolloClient/client";

// Utils
import {
  getRubricDescriptorLabel,
  getFormattedResponseMemoized,
  getInputForAIResponse,
  getAssessmentToolsFromQuestion,
  getContentToSend,
} from "./Utils";
import { generateRandomId } from "Utils";
import { getGroupedFields } from "../components/PrerequisiteSelection/Utils";
import {
  isWorksheetMutationsV2Enabled,
  isWorksheetQueriesV2Enabled,
  getIsLearningGoalsRevampEnabled,
} from "Worksheet/modules/WorksheetFeatureFlags";

// Modules
import { getPlannerFieldMutationParams } from "UnitPlans/modules/UnitPlanModule";
import { getAssessmentToolNodeDetailsFromCacheV2 } from "modules/CommonGraphqlHelpers";
import {
  getAIConversationsFromCache,
  writeAIConversationsInCache,
  getQuestionFromCache,
  writeQuestionInCache,
  getQuestionFromCacheV2,
  writeQuestionInCacheV2,
} from "Worksheet/modules/WorksheetGraphqlHelpers";
import { createAssessmentTool } from "AssessmentToolSetup/modules/AssessmentToolSetupModule";
import { updateAssessmentInCache } from "Assessments/modules/AssessmentModule";
import {
  bulkUpdateRubricDescriptors,
  bulkCreateRubricIndicators,
} from "Rubric/modules/RubricModule";
import { bulkCreateChecklistItems } from "Checklist/modules/ChecklistModule";
import { bulkCreateSinglePointRubricStandard } from "SinglePointRubric/modules/SinglePointRubricModule";
import {
  getSelectedCurriculumId,
  getCurrentCurriculumProgramType,
  getSelectedAcademicYearId,
} from "modules/CommonSelectors";
import {
  getAssessmentDetailsFromCache,
  writeAssessmentDetailsToCache,
} from "Assessments/modules/AssessmentGraphqlHelpers";
import { updateUnitPlanInCache } from "IBPlanner/modules/IBPlannerModule";
import {
  createQuestionField,
  createWorksheetQuestionAI,
  createWorksheetQuestionAIV2,
  updateLoadingState,
  replaceWorksheetQuestionAI,
  replaceWorksheetQuestionAIV2,
} from "Worksheet/modules/WorksheetModules";
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";
import { getSelectedLearningCourseId } from "LearningCourse/modules/Utils";

// Constants and services
import {
  RUBRIC,
  CHECKLIST,
  SINGLE_POINT_RUBRIC,
  ASSESSMENT,
  WRITING,
  STREAMING,
  ELEMENT_TYPE_UNIT_PLAN,
  MYP_OBJECTIVE_RUBRIC,
} from "Constants/stringConstants";
import {
  getAIResponse,
  updateAIMessage,
  updateAIConversation,
} from "modules/Services";
import { updateField } from "modules/FieldUpdateThunks";
import { getResolvedOptimisticValue } from "modules/ElementValueUtils";
import { ASSESSMENT_TOOLS_OBJ } from "@toddle-edu/common-utils";
import { createAIConversationMutation } from "modules/CommonMutations";
import { FIELD_UID_ASSESSMENT_TOOL } from "Worksheet/modules/WorksheetConstants";
import { CF_MYP_CONTENT } from "./Constants";
import { TEMP_ID } from "AppComponents/AIContextBar/modules/Constants";
import { bulkEditMypObjectiveRubricDescriptor } from "modules/AssessmentTool/AssessmentToolModule";
import {
  compareMYPCriteriaNodeText,
  getAssessmentAndUnitPlanCombinedNodes,
  getMYPObjectivesData,
} from "../../LessonPlanningAssistant/modules/Utils";
import { createAIMessage } from "../../LessonPlanningAssistant/modules/LessonPlanningAssistantModule";

export let cancelRequest;

const updateAssessmentFieldLocal = ({
  t,
  params,
  extraParams = {},
  field_list = {},
  portalType,
  resourceFields,
  isDebounce = true,
}) => {
  return async dispatch => {
    _.map(params, (obj, key) => {
      const fieldObj = _.find(resourceFields, { uid: key }) || {};

      const fieldTemplateObj = field_list?.[key];
      const { id, value: oldValue } = fieldObj;
      const value = { id, value: obj };

      dispatch(
        updateAssessmentInCache({
          fieldUID: key,
          params: { ...value, uid: key },
          fieldObj,
          fieldTemplateObj,
          extraParams,
        })
      );

      const mutationParams = getPlannerFieldMutationParams({
        id,
        oldValue,
        hasAddRemoveValues: false,
        newValue: obj,
      });

      dispatch(
        updateField({
          key: id,
          params: {
            ...(mutationParams || {}),
            portalType,
          },
          type: ASSESSMENT,
          isDebounce,
          t,
        })
      );
    });
  };
};

export const updateAssignmentField = params => {
  const {
    key,
    content,
    assessmentDetails,
    selectedAssessmentField,
    metadata,
  } = params;

  return async dispatch => {
    const resourceFields = _.get(assessmentDetails, "allFields", []);
    const field = selectedAssessmentField;

    if (ASSESSMENT_TOOLS_OBJ?.[field] && key !== "add_to_le") {
      const assessmentTools = _.get(
        assessmentDetails,
        "assessmentTools.edges",
        []
      );

      const selectedAssessmentTool = _.find(assessmentTools, ({ node }) => {
        return _.get(node, "assessmentToolType") === field;
      });

      const assessmentToolItem = _.get(
        selectedAssessmentTool,
        "node.assessmentToolItem",
        {}
      );

      const selectedAssessmentToolIndex = _.findIndex(
        assessmentTools,
        ({ node }) => {
          return _.get(node, "assessmentToolType") === field;
        }
      );

      const insertAt =
        selectedAssessmentToolIndex === -1 ? null : selectedAssessmentToolIndex;

      const assessmentToolInput = {
        parentId: _.get(assessmentDetails, "id"),
        parentType: ASSESSMENT,
        id: _.get(selectedAssessmentTool, "node.assessmentToolItem.id"),
        type: field,
        isGlobal: false,
        assessmentId: _.get(assessmentDetails, "id"),
        field,
      };

      switch (field) {
        case RUBRIC:
          {
            const {
              criterias = [],
              indicators = [],
              descriptors = [],
            } = dispatch(
              convertAIResponseToToolStructure({
                metadata: { fieldType: selectedAssessmentField },
                content: await getFormattedResponseMemoized({
                  content: content,
                  fieldType: selectedAssessmentField,
                }),
                assessmentDetails,
              })
            );

            switch (key) {
              case "add_below":
                {
                  const response = await dispatch(
                    bulkCreateRubricIndicators({
                      indicators: _.map(indicators, indicator => {
                        return {
                          rubricId: _.get(
                            selectedAssessmentTool,
                            "node.assessmentToolItem.id"
                          ),
                          label: _.get(indicator, "label"),
                        };
                      }),
                    })
                  );

                  const flatDescriptors = _.flatMap(
                    response,
                    "descriptors",
                    []
                  );

                  dispatch(
                    bulkUpdateRubricDescriptors({
                      descriptors: _.map(flatDescriptors, descriptor => {
                        const label = getRubricDescriptorLabel({
                          criterias,
                          indicators,
                          descriptors,
                          descriptor,
                        });

                        return {
                          id: _.get(descriptor, "id"),
                          label,
                        };
                      }),
                    })
                  );
                }
                break;
              default: {
                const id = await dispatch(
                  createAssessmentTool({
                    ...assessmentToolInput,
                    assessmentId: _.get(assessmentDetails, "id"),
                    extraParams: {
                      criterias: _.map(criterias, criteria =>
                        _.get(criteria, "label")
                      ),
                      indicators: _.map(indicators, indicator =>
                        _.pick(indicator, ["label"])
                      ),
                      insertAt,
                      removeToolId: _.get(assessmentToolInput, "id"),
                    },
                  })
                );

                const rubric = getAssessmentToolNodeDetailsFromCacheV2({
                  id,
                  type: field,
                });

                dispatch(
                  bulkUpdateRubricDescriptors({
                    descriptors: _.map(rubric?.descriptors, descriptor => {
                      const label = getRubricDescriptorLabel({
                        criterias,
                        indicators,
                        descriptors,
                        descriptor,
                      });

                      return {
                        id: _.get(descriptor, "id"),
                        label,
                      };
                    }),
                  })
                );
              }
            }
          }
          break;
        case CHECKLIST:
          {
            const { checklistOptionItems = [], checklistItems = [] } = dispatch(
              convertAIResponseToToolStructure({
                metadata: { fieldType: selectedAssessmentField },
                content: await getFormattedResponseMemoized({
                  content: content,
                  fieldType: selectedAssessmentField,
                }),
                assessmentDetails,
              })
            );

            switch (key) {
              case "add_below":
                {
                  const items = _.map(checklistItems, item => {
                    return {
                      checklistId: _.get(assessmentToolItem, "id"),
                      label: _.get(item, "label"),
                    };
                  });

                  await dispatch(
                    bulkCreateChecklistItems({
                      items,
                    })
                  );
                }
                break;
              default: {
                await dispatch(
                  createAssessmentTool({
                    ...assessmentToolInput,
                    assessmentId: _.get(assessmentDetails, "id"),
                    extraParams: {
                      checklistOptionItems: _.map(checklistOptionItems, item =>
                        _.pick(item, "label")
                      ),
                      checklistItems: _.map(checklistItems, item =>
                        _.pick(item, ["label"])
                      ),
                      insertAt,
                      removeToolId: _.get(assessmentToolInput, "id"),
                    },
                  })
                );
              }
            }
          }
          break;
        case SINGLE_POINT_RUBRIC:
          {
            const { criterias = [], standards = [] } = dispatch(
              convertAIResponseToToolStructure({
                metadata: { fieldType: selectedAssessmentField },
                content: await getFormattedResponseMemoized({
                  content: content,
                  fieldType: selectedAssessmentField,
                }),
                assessmentDetails,
              })
            );

            switch (key) {
              case "add_below":
                {
                  await dispatch(
                    bulkCreateSinglePointRubricStandard({
                      input: _.map(standards, standard => {
                        return {
                          rubricId: _.get(assessmentToolItem, "id"),
                          title: _.get(standard, "title", ""),
                          subTitle: _.get(standard, "subTitle", ""),
                        };
                      }),
                    })
                  );
                }
                break;
              default: {
                await dispatch(
                  createAssessmentTool({
                    ...assessmentToolInput,
                    assessmentId: _.get(assessmentDetails, "id"),
                    extraParams: {
                      criterias,
                      standards,
                      insertAt,
                      removeToolId: _.get(assessmentToolInput, "id"),
                    },
                  })
                );
              }
            }
          }
          break;
        case MYP_OBJECTIVE_RUBRIC: {
          // 1. get objective ids for the AI generated rubric
          const { objectiveIds, updateObjectives } = getMYPObjectivesData({
            field,
            content,
            assessmentDetails,
            metadata,
          });

          // 2. store the objective Ids
          if (!_.isEmpty(objectiveIds) && updateObjectives) {
            await dispatch(
              updateAssessmentFieldLocal({
                ...params,
                params: {
                  [`${ASSESSMENT_TOOLS_OBJ[field]?.objectiveFieldKey}`]: objectiveIds,
                },
                resourceFields,
              })
            );
          }

          // 3. create the assessment tool
          const id = await dispatch(
            createAssessmentTool({
              ...assessmentToolInput,
              assessmentId: _.get(assessmentDetails, "id"),
              assessmentToolData: {
                objectiveIds,
                insertAt,
                removeToolId: _.get(assessmentToolInput, "id"),
              },
            })
          );

          if (id) {
            const tool = getAssessmentToolNodeDetailsFromCacheV2({
              id,
              type: field,
            });

            // 4. get the descriptors from the AI generated rubric table
            const descriptors = dispatch(
              convertAIResponseToToolStructure({
                metadata: {
                  ...(metadata || {}),
                  fieldType: selectedAssessmentField,
                },
                content: getFormattedResponseMemoized({
                  content: content,
                  fieldType: selectedAssessmentField,
                  assessmentDetails,
                }),
                tool,
                assessmentDetails,
              })
            );

            // 5. update the descriptors of the rubric
            if (!_.isEmpty(descriptors)) {
              await dispatch(bulkEditMypObjectiveRubricDescriptor(descriptors));
            }
          }
          break;
        }
      }
      dispatch(
        updateAssessmentFieldLocal({
          ...params,
          params: { measureAssessing: selectedAssessmentField },
          resourceFields,
        })
      );
    } else {
      dispatch(updateAssessmentFieldLocal({ ...params, resourceFields }));
    }
  };
};

export const convertAIResponseToToolStructure = params => {
  return () => {
    const { metadata, content = {}, tool, assessmentDetails } = params || {};

    const field = metadata?.fieldType;

    try {
      switch (field) {
        case RUBRIC: {
          const rubric = {};

          rubric["indicators"] = _.get(content, "criterias", []);
          rubric["criterias"] = _.get(content, "descriptorLevels", []);
          rubric["descriptors"] = _.map(
            _.get(content, "descriptors", []),
            descriptor => {
              const {
                label,
                criteriaId: indicatorId,
                descriptorLevelId: criteriaId,
              } = descriptor || {};

              return {
                id: generateRandomId(),
                label,
                criteria: {
                  id: criteriaId,
                },
                indicator: {
                  id: indicatorId,
                },
              };
            }
          );

          return rubric;
        }
        case CHECKLIST: {
          return {
            checklistOptionItems: _.get(content, "checklistOptions", []),
            checklistItems: _.get(content, "criterias", []),
          };
        }

        case SINGLE_POINT_RUBRIC: {
          return {
            criterias: _.get(content, "criterias", []),
            standards: _.map(
              _.get(content, "standards", []),
              (standard, index) => {
                return {
                  ...standard,
                  id: index,
                };
              }
            ),
          };
        }
        case MYP_OBJECTIVE_RUBRIC: {
          const descriptors = _.get(tool, "descriptors", []);
          const descriptorsGroup = _.groupBy(descriptors, "objective.id");

          const nodes = getAssessmentAndUnitPlanCombinedNodes({
            assessmentDetails,
            type: field,
          });

          const result = [];

          /**
           * example of content =>
           * [[6],[6],[6],[6]]
           * [[6],[6]]
           * [[6],[6],[6]]
           * [[6]]
           *
           *
           * [[6],[6],[6],[6]] -> represent number of criteria selected for a objective (A, B, C, D)
           * [6] -> represents each row (criteria) of the table
           * 6 -> represents each cell (descriptor) of the table
           */

          const objectiveIds = [];

          _.forEach(content, rows => {
            _.forEach(rows, row => {
              // achievementLevel will be empty for the first cell of each row
              // it will help us to identify the objective id

              if (_.isEmpty(_.first(row)?.achievementLevel)) {
                const objectiveId = _.find(descriptors, descriptor => {
                  const depthTwoNodeLabel = _.get(
                    _.find(nodes, {
                      id: _.get(descriptor, "objective.parent"),
                      depth: 2,
                    }),
                    "label"
                  );

                  return (
                    !_.includes(objectiveIds, descriptor?.objective?.id) &&
                    compareMYPCriteriaNodeText({
                      originalCriteria: _.get(descriptor, "objective.label"),
                      aiGeneratedCriteria: _.first(row)?.label,
                      metadata,
                      subject: _.first(row)?.subject,
                      depthTwoNodeLabel,
                    })
                  );
                })?.objective?.id;

                if (objectiveId) objectiveIds.push(objectiveId);
              }

              const objectiveId = _.last(objectiveIds);

              // _.tail will remove the first cell of each row (where achievementLevel is empty)
              const rowTail = _.tail(row);

              _.forEach(
                _.get(descriptorsGroup, objectiveId, []),
                descriptor => {
                  const currentDescriptor = _.find(rowTail, {
                    achievementLevel: descriptor?.achievementLevel?.label,
                  });
                  result.push({
                    id: descriptor?.id,
                    label: currentDescriptor?.label,
                  });
                }
              );
            });
          });

          return result;
        }
      }
    } catch (e) {
      return null;
    }
  };
};

export const onSendQuery = params => {
  const {
    t,
    query,
    leId,
    aiConversationId,
    selectedAction,
    setQuery,
    setSelectedAction,
    setCurrentAIState,
    gradeId,
    course,
    language,
    context = {},
    fieldList,
    unitPlanFieldList,
    unitPlanDetails,
    lastMessageId,
    isUserMessageEdited,
    editUserPromptObj,
    setEditUserPromptObj,
    messages,
    assessmentDetails,
    excludedFields,
    entityType,
    stepList,
    unitPlanStepList,
    triggeredFromGenWithAIModal = false,
    questionId,
    isQuestionAssistant,
    conversationMetadata,
    conversationAttachments,
    learningGoalsFields,
    setIsAnalysing,
    setSendButtonParams = _.noop,
    sendButtonParams = {},
    shouldNotProcessAttachments = false,
  } = params;

  let currentParentMessageId = lastMessageId;

  return async (dispatch, getState) => {
    try {
      const state = getState();

      const isEnglishLanguageSelected = language === "en";
      const shouldProcessAttachments =
        !shouldNotProcessAttachments && !_.isEmpty(conversationAttachments);
      const learningCourseId = getSelectedLearningCourseId(state);

      const { content, inputText, customInstructions } = getContentToSend({
        t,
        selectedAction,
        isEnglishLanguageSelected,
        editUserPromptObj,
        query,
        context,
        conversationAttachments,
      });

      if (_.isEmpty(content)) return;

      // 1. reset state so that input box becomes empty
      setQuery?.("");

      const userMessageMetadata = {
        isUserInputMessage: _.isEmpty(selectedAction),
      };
      const allMessagesImageUrls = _.compact(
        _.flatMap(messages, ({ node }) => {
          const { metadata } = node || {};
          return _.get(metadata, "imageUrls", []);
        })
      );
      const imageUrls = _.map(
        _.filter(conversationAttachments, attachment => {
          const { url, type } = attachment || {};
          return type === "IMAGE" && !_.includes(allMessagesImageUrls, url);
        }),
        "url"
      );

      const shouldStoreImageUrl =
        !shouldNotProcessAttachments &&
        (_.isEmpty(messages) ||
          !_.some(messages, ({ node }) => {
            const { metadata } = node || {};

            return _.isEmpty(_.xor(imageUrls, metadata?.imageUrls));
          }));

      if (shouldStoreImageUrl && !_.isEmpty(imageUrls)) {
        _.set(userMessageMetadata, "imageUrls", imageUrls);
      }

      if (isUserMessageEdited) {
        const { lastUserMessageId } = editUserPromptObj;
        const lastUserMessage = _.find(
          messages,
          ({ node }) => _.get(node, "id") === lastUserMessageId
        );
        const assistantResponseMessage = _.find(
          messages,
          ({ node }) => _.get(node, "parentMessage.id") === lastUserMessageId
        );
        currentParentMessageId = lastUserMessage.id;

        const oldConversation = getAIConversationsFromCache({
          id: aiConversationId,
        });

        const assistantResponseMessageId = _.get(
          assistantResponseMessage,
          "node.id"
        );

        const filteredEdges = _.filter(
          _.get(oldConversation, "messages.edges", []),
          ({ node }) =>
            !_.includes(
              [lastUserMessageId, assistantResponseMessageId],
              node?.id
            )
        );

        const updatedConversation = {
          ...oldConversation,
          messages: {
            ...oldConversation.messages,
            edges: filteredEdges,
            totalCount: _.size(filteredEdges),
          },
        };

        writeAIConversationsInCache({
          variables: {
            id: aiConversationId,
          },
          data: {
            node: updatedConversation,
          },
        });

        await dispatch(
          updateAIMessage({
            variables: {
              input: {
                id: lastUserMessageId,
                isArchived: true,
              },
            },
          })
        );
        if (assistantResponseMessage) {
          await dispatch(
            updateAIMessage({
              variables: {
                input: {
                  id: _.get(assistantResponseMessage, "node.id"),
                  isArchived: true,
                },
              },
            })
          );
        }

        setEditUserPromptObj?.({
          isEditMode: false,
          userPrompt: "",
        });
      }

      // 2. create a message from user input
      if (
        _.get(selectedAction, "createUserMessage", true) &&
        _.get(sendButtonParams, "createUserMessage", true)
      ) {
        await dispatch(
          createAIMessage({
            variables: {
              input: {
                conversationId: aiConversationId,
                authorType: "user",
                attachments: [],
                content: inputText,
                metadata: userMessageMetadata,
                parentMessageId: currentParentMessageId,
              },
            },
          })
        );
      }

      // 3. show writing... animation
      setCurrentAIState?.(WRITING);

      const hasTempAttachment = _.some(conversationAttachments, ({ id }) =>
        _.includes(id, TEMP_ID)
      );

      if (hasTempAttachment) {
        setSendButtonParams(prev => ({
          ...prev,
          content,
          attachmentHasTempId: true,
          checkLoadingState: false,
          createUserMessage: false,
        }));
        return;
      }

      // 4. generate AI response
      const conversation = getAIConversationsFromCache({
        id: aiConversationId,
      });

      const parentMessageId = _.get(conversation, "messages.edges[0].node.id");

      const id = generateRandomId();
      let responseContent = {};

      const response = await dispatch(
        getAIResponse({
          variables: {
            input: getInputForAIResponse({
              leId,
              content: content ?? "",
              selectedAction,
              gradeId,
              entityType,
              course,
              learningCourseId,
              curriculumProgramId: getSelectedCurriculumId(getState()),
              curriculumProgramType: getCurrentCurriculumProgramType(
                getState()
              ),
              context,
              assessmentDetails,
              unitPlanFields: _.get(unitPlanDetails, "unitPlanFields"),
              fieldList,
              unitPlanFieldList,
              excludedFields,
              questionId,
              isQuestionAssistant,
              conversationMetadata,
              customInstructions,
              learningGoalsFields,
              conversationId: aiConversationId,
              shouldProcessAttachments,
              imageUrls: shouldStoreImageUrl ? imageUrls : [],
            }),
          },
          getCancelToken: token => (cancelRequest = token),
          onDownloadProgress: params => {
            const {
              action,
              fieldType,
              content,
              usageMetricsId,
              userInputFlagged,
              model,
              temperature,
              intentUsageMetricsId,
              prerequisitesFields,
              skipGPTCall = false,
              latestChunkStatus,
              languageCode,
            } = params || {};

            // showing analysing loader if `latestChunkStatus` is `TOOL_INDICATION`
            setIsAnalysing?.(prevIsAnalysing => {
              if (
                !prevIsAnalysing &&
                _.isEqual("TOOL_INDICATION", latestChunkStatus)
              ) {
                return true;
              } else if (
                prevIsAnalysing &&
                !_.isEqual("TOOL_INDICATION", latestChunkStatus)
              ) {
                return false;
              }

              return prevIsAnalysing;
            });

            if (!_.isEmpty(prerequisitesFields)) {
              const groupedFields = getGroupedFields({
                t,
                fields: prerequisitesFields,
                fieldList,
                unitPlanFieldList,
                unitPlanDetails,
                assessmentDetails,
                stepList,
                unitPlanStepList,
              });

              dispatch(
                createAIMessage({
                  variables: {
                    input: {
                      conversationId: aiConversationId,
                      authorType: "prerequisite",
                      content,
                      attachments: [],
                      metadata: {
                        fields: groupedFields,
                        fieldType,
                        action,
                        skipGPTCall,
                        language: languageCode || language,
                      },
                    },
                  },
                })
              );

              return;
            }

            // when user cancels the request, responseContent will contain the response generated till that point
            responseContent = params;

            const createAiMessageNode = {
              node: {
                id,
                role: null,
                authorType: "assistant",
                content: userInputFlagged
                  ? t("common:user_input_flagged_msg")
                  : content,
                metadata: {
                  fieldType,
                  userInputFlagged,
                  model,
                  temperature,
                  intentUsageMetricsId,
                  language: languageCode || language,
                },
                createdBy: null,
                votes: {
                  myVote: null,
                  __typename: "VoteConnection",
                },
                usageMetricsId: usageMetricsId || null,
                parentMessage: {
                  id: null,
                  __typename: "AiMessage",
                },
                attachments: [],
                __typename: "AiMessage",
              },
              __typename: "AiMessageEdge",
            };

            const dataToSend = update(conversation, {
              messages: {
                edges: {
                  $unshift: [createAiMessageNode],
                },
                totalCount: {
                  $set: _.get(conversation, "messages.totalCount", 0) + 1,
                },
              },
            });

            writeAIConversationsInCache({
              variables: {
                id: aiConversationId,
              },
              data: {
                node: dataToSend,
              },
            });

            setCurrentAIState?.(STREAMING);
          },
        })
      );

      // 5. hide "Stop generation" button and reset local states
      if (triggeredFromGenWithAIModal) {
        setCurrentAIState?.("END");
        setSendButtonParams({});
        // returning from here for `Generate using AI` as we don't need to add ai generated messages in conversation
        return;
      } else {
        setCurrentAIState?.("");
        setSelectedAction?.({});
        setIsAnalysing?.(false);
        setSendButtonParams({});
      }

      // 6. create a message of the generated response from AI
      const {
        content: aiResponse,
        fieldType,
        usageMetricsId,
        model,
        temperature,
        intentUsageMetricsId,
        userInputFlagged = false,
        languageCode,
      } = response || responseContent || {};

      if (!_.isEmpty(aiResponse) || userInputFlagged) {
        const content = userInputFlagged
          ? t("common:user_input_flagged_msg")
          : aiResponse;

        const response = await dispatch(
          createAIMessage({
            variables: {
              input: {
                conversationId: aiConversationId,
                authorType: "assistant",
                content,
                attachments: [],
                metadata: {
                  fieldType,
                  userInputFlagged,
                  model,
                  temperature,
                  intentUsageMetricsId,
                  language: languageCode || language,
                },
                usageMetricsId,
                parentMessageId,
              },
            },
            writeToCache: false,
          })
        );

        const aiConversation = getAIConversationsFromCache({
          id: aiConversationId,
        });
        const updatedData = update(aiConversation, {
          messages: {
            edges: {
              0: {
                node: {
                  $set: _.get(response, "data.ai.createAiMessage.response", {}),
                },
              },
            },
          },
        });
        writeAIConversationsInCache({
          variables: {
            id: aiConversationId,
          },
          data: {
            node: updatedData,
          },
        });
      }
    } catch (e) {
      setCurrentAIState?.("");
      setIsAnalysing?.(false);
      setSendButtonParams({});
    }
  };
};

export const createAIConversation = ({
  input,
  isQuestionAssistant = false,
}) => {
  return async (_dispatch, getState) => {
    try {
      const response = await client.mutate({
        mutation: createAIConversationMutation,
        variables: {
          input,
        },
        update: (
          cache,
          {
            data: {
              ai: { createAiConversation },
            },
          }
        ) => {
          const conversationId = _.get(createAiConversation, "response.id");
          const entityId = _.get(input, "entityId");

          const aiConversation = {
            edges: [
              {
                node: {
                  id: conversationId,
                  __typename: "AiConversation",
                },
                __typename: "AiConversationEdge",
              },
            ],
            __typename: "AiConversationConnection",
          };

          if (isQuestionAssistant) {
            const isQueriesV2Enabled = isWorksheetQueriesV2Enabled();

            const getQuestion = isQueriesV2Enabled
              ? getQuestionFromCacheV2
              : getQuestionFromCache;
            const writeQuestion = isQueriesV2Enabled
              ? writeQuestionInCacheV2
              : writeQuestionInCache;

            const cacheQuestionData = getQuestion({ id: entityId });

            const updatedCacheData = {
              ...cacheQuestionData,
              aiConversation,
            };

            writeQuestion({
              id: entityId,
              data: updatedCacheData,
            });
          } else {
            const academicYearId = getSelectedAcademicYearId(getState());
            const collaboratorsFilter = {
              userAcademicYearId: academicYearId,
            };

            const assessmentDetails = getAssessmentDetailsFromCache({
              id: entityId,
              collaboratorsFilter,
            });

            const node = {
              ...assessmentDetails,
              aiConversation,
              __typename: "Assessment",
            };

            writeAssessmentDetailsToCache({
              variables: {
                id: entityId,
                collaboratorsFilter,
              },
              data: { node },
            });
          }

          return conversationId;
        },
      });
      return _.get(response, "data.ai.createAiConversation.response", {});
    } catch (err) {
      // no need to show error toast
      return {};
    }
  };
};

export const updateUnitPlanField = ({
  t,
  params,
  unitPlanFields = [],
  extraParams = {},
  field_list = {},
  isMutation = true,
  hasAddRemoveValues = false,
  isDebounce = true,
  unitPlanId,
}) => {
  return async dispatch => {
    _.map(params, async (obj, key) => {
      const fieldObj = _.find(unitPlanFields, { uid: key }) || {};
      const { id, value: oldValue } = fieldObj;
      const { valuesToAdd, valuesToRemove } = extraParams || {};

      const value = {
        id,
        value: obj,
      };

      dispatch(
        updateUnitPlanInCache({
          fieldUID: key,
          params: value,
          fieldObj,
          extraParams,
          fieldTemplateObj: field_list[key],
        })
      );

      if (isMutation) {
        dispatch(
          updateField({
            key: id,
            params: {
              unitPlanId,
              ...getPlannerFieldMutationParams({
                id,
                oldValue,
                hasAddRemoveValues,
                valuesToAdd,
                valuesToRemove,
                newValue: _.get(value, "value", []),
                fieldUID: key,
              }),
            },
            type: ELEMENT_TYPE_UNIT_PLAN,
            isDebounce,
            t,
          })
        );
      }
    });
  };
};

/**
 * Adds a question to the worksheet using AI generation
 * @param {Object} params - The parameters for adding a question
 * @param {string} params.parentId - The parent ID
 * @param {string} params.parentType - The parent type
 * @param {string} params.parentResourceId - The parent resource ID
 * @param {string} params.putAfterItemId - The ID to put the item after
 * @param {string} params.aiMessageId - The AI message ID
 * @param {number} params.aiSequence - The AI sequence number
 * @param {boolean} params.hasSection - Whether it has a section
 * @param {string} params.gradeId - The grade ID
 * @returns {Function} Redux thunk function
 */
export const addQuestionToWorksheet = ({
  parentId,
  parentType,
  parentResourceId,
  putAfterItemId,
  putAfterItemIdV2,
  aiMessageId,
  aiSequence,
  gradeId,
  lastSectionId,
  showLoader = false,
}) => {
  return async dispatch => {
    const isMutationsV2Enabled = isWorksheetMutationsV2Enabled();
    const isQueriesV2Enabled = isWorksheetQueriesV2Enabled();

    if (showLoader) {
      dispatch(updateLoadingState(true));
    }

    if (isMutationsV2Enabled) {
      // Use V2 mutation (new version)
      await dispatch(
        createQuestionV2({
          parentId,
          parentResourceId,
          putAfterItemId: putAfterItemIdV2,
          aiMessageId,
          aiSequence,
          gradeId,
          lastSectionId,
          isQueriesV2Enabled,
        })
      );
    } else {
      // Use V1 mutation (legacy version)
      await dispatch(
        createQuestionV1({
          parentId,
          parentType,
          parentResourceId,
          putAfterItemId,
          aiMessageId,
          aiSequence,
          gradeId,
        })
      );
    }

    if (showLoader) {
      dispatch(updateLoadingState(false));
    }
  };
};

/**
 * Creates a question using the V2 mutation (new version)
 * @param {Object} params - The parameters for creating a question
 * @returns {Function} Redux thunk function
 */
const createQuestionV2 = ({
  parentId,
  putAfterItemId,
  aiMessageId,
  aiSequence,
  gradeId,
  lastSectionId,
  isQueriesV2Enabled,
}) => {
  return async dispatch => {
    const input = {
      worksheetId: parentId,
      parentId: lastSectionId,
      parentType: "QUESTION",
      putAfterQuestionId: putAfterItemId,
      aiMessageId,
      aiSequence: aiSequence,
      gradeId,
    };

    return await dispatch(
      createWorksheetQuestionAIV2({
        input,
        refetch: true,
        isQueriesV2Enabled,
      })
    );
  };
};

/**
@deprecated Use createQuestionV2 instead
 * Creates a question using the V1 mutation (legacy version)
 * @param {Object} params - The parameters for creating a question
 * @returns {Function} Redux thunk function
 */
const createQuestionV1 = ({
  parentId,
  parentType,
  parentResourceId,
  putAfterItemId,
  aiMessageId,
  aiSequence,
  gradeId,
}) => {
  return async dispatch => {
    const input = {
      organizationResource: {
        parentId,
        parentType,
        parentResourceId,
        organizationResources: [
          {
            type: "WORKSHEET_RESOURCE",
            resourceType: "QUESTION",
          },
        ],
        shouldHandleSequence: true,
        putAfterItemId,
      },
      aiMessageId,
      aiSequence,
      gradeId,
    };

    return await dispatch(
      createWorksheetQuestionAI({
        parentId,
        parentType,
        input,
      })
    );
  };
};

/**
 * @deprecated Use `replaceQuestionToWorksheetV2` instead.
 * This is kept for backward compatibility.
 * It will be removed in future versions.
 */
export const replaceQuestionToWorksheet = ({
  questionIdToReplace,
  parentId,
  parentType,
  parentResourceId,
  putAfterItemId,
  aiMessageId,
  aiSequence,
  questionResourceId,
  gradeId,
}) => {
  return async dispatch => {
    dispatch(updateLoadingState(true));

    const input = {
      questionIdToReplace,
      questionCreationInput: {
        organizationResource: {
          parentId,
          parentType,
          parentResourceId,
          organizationResources: [
            {
              type: "WORKSHEET_RESOURCE",
              resourceType: "QUESTION",
            },
          ],
          shouldHandleSequence: true,
          putAfterItemId,
        },
        aiMessageId,
        aiSequence,
        gradeId,
      },
    };

    // call replace mutation
    await dispatch(
      replaceWorksheetQuestionAI({
        parentId,
        parentType,
        input,
        questionResourceId,
        parentResourceId,
        questionIdToReplace,
      })
    );

    dispatch(updateLoadingState(false));
  };
};

export const replaceQuestionToWorksheetV2 = ({
  questionIdToReplace,
  parentItemId,
  putAfterQuestionId,
  aiMessageId,
  aiSequence,
  gradeId,
}) => {
  return async (dispatch, getState) => {
    const state = getState();
    const parentId = _.get(state, "worksheet.parentEntity.id", null);

    const isQueriesV2Enabled = isWorksheetQueriesV2Enabled();

    dispatch(updateLoadingState(true));

    const input = {
      questionIdToReplace,
      questionCreationInput: {
        worksheetId: parentId,
        parentId: parentItemId,
        parentType: "QUESTION",
        putAfterQuestionId,
        aiMessageId,
        aiSequence,
        gradeId,
      },
    };

    // call replace mutation
    await dispatch(
      replaceWorksheetQuestionAIV2({
        input,
        questionIdToReplace,
        isQueriesV2Enabled,
      })
    );

    dispatch(updateLoadingState(false));
  };
};

export const addAssessmentToolToQuestion = params => {
  const {
    content,
    assessmentToolType,
    questionId,
    portalType,
    assessmentId,
  } = params;

  return async dispatch => {
    dispatch(updateLoadingState(true));
    const isQueriesV2Enabled = isWorksheetQueriesV2Enabled();

    const getQuestion = isQueriesV2Enabled
      ? getQuestionFromCacheV2
      : getQuestionFromCache;

    const questionFromCachedData = getQuestion({ id: questionId });
    const questionFields = _.get(questionFromCachedData, "fields", []);

    const measureAssessingField = _.find(questionFields, {
      uid: FIELD_UID_ASSESSMENT_TOOL,
    });

    // * creating `measureAssessing` field if not present on questionFields. Otherwise, assessment tools won't visible
    if (_.isEmpty(measureAssessingField)) {
      await dispatch(
        createQuestionField({
          uids: [FIELD_UID_ASSESSMENT_TOOL],
          portalType,
          questionId,
        })
      );
    }

    const assessmentTools = getAssessmentToolsFromQuestion(questionId);

    const selectedAssessmentTool = _.find(assessmentTools, ({ node }) => {
      return _.get(node, "assessmentToolType") === assessmentToolType;
    });

    const selectedAssessmentToolIndex = _.findIndex(
      assessmentTools,
      ({ node }) => {
        return _.get(node, "assessmentToolType") === assessmentToolType;
      }
    );

    const insertAt =
      selectedAssessmentToolIndex === -1 ? null : selectedAssessmentToolIndex;

    const assessmentToolInput = {
      parentId: questionId,
      assessmentId: questionId,
      parentType: "QUESTION",
      id: _.get(selectedAssessmentTool, "node.assessmentToolItem.id"),
      type: assessmentToolType,
      isGlobal: false,
      portalType,
    };

    switch (assessmentToolType) {
      case RUBRIC:
        {
          const {
            criterias = [],
            indicators = [],
            descriptors = [],
          } = dispatch(
            convertAIResponseToToolStructure({
              metadata: { fieldType: assessmentToolType },
              content: await getFormattedResponseMemoized({
                content: content,
                fieldType: assessmentToolType,
              }),
            })
          );

          const id = await dispatch(
            createAssessmentTool({
              ...assessmentToolInput,
              extraParams: {
                assessmentId: assessmentId,
                criterias: _.map(criterias, criteria =>
                  _.get(criteria, "label")
                ),
                indicators: _.map(indicators, indicator =>
                  _.pick(indicator, ["label"])
                ),
                insertAt,
                removeToolId: _.get(assessmentToolInput, "id"),
              },
            })
          );

          const rubric = getAssessmentToolNodeDetailsFromCacheV2({
            id,
            type: assessmentToolType,
          });

          dispatch(
            bulkUpdateRubricDescriptors({
              descriptors: _.map(rubric?.descriptors, descriptor => {
                const label = getRubricDescriptorLabel({
                  criterias,
                  indicators,
                  descriptors,
                  descriptor,
                });

                return {
                  id: _.get(descriptor, "id"),
                  label,
                };
              }),
            })
          );
        }
        break;
      case CHECKLIST:
        {
          const { checklistOptionItems = [], checklistItems = [] } = dispatch(
            convertAIResponseToToolStructure({
              metadata: { fieldType: assessmentToolType },
              content: await getFormattedResponseMemoized({
                content: content,
                fieldType: assessmentToolType,
              }),
            })
          );

          await dispatch(
            createAssessmentTool({
              ...assessmentToolInput,
              extraParams: {
                assessmentId: assessmentId,
                checklistOptionItems: _.map(checklistOptionItems, item =>
                  _.pick(item, "label")
                ),
                checklistItems: _.map(checklistItems, item =>
                  _.pick(item, ["label"])
                ),
                insertAt,
                removeToolId: _.get(assessmentToolInput, "id"),
              },
            })
          );
        }
        break;
      case SINGLE_POINT_RUBRIC:
        {
          const { criterias = [], standards = [] } = dispatch(
            convertAIResponseToToolStructure({
              metadata: { fieldType: assessmentToolType },
              content: await getFormattedResponseMemoized({
                content: content,
                fieldType: assessmentToolType,
              }),
            })
          );

          await dispatch(
            createAssessmentTool({
              ...assessmentToolInput,
              extraParams: {
                assessmentId: assessmentId,
                criterias,
                standards,
                insertAt,
                removeToolId: _.get(assessmentToolInput, "id"),
              },
            })
          );
        }
        break;
    }
    dispatch(updateLoadingState(false));
  };
};

// * to update conversation's allFields
export const updateConversationField = ({
  extraParams,
  fieldUID,
  params,
  fieldList,
  conversationMetadata,
  aiConversationId,
  updatedConversationFields = [],
}) => {
  return async dispatch => {
    try {
      const isLearningGoalsRevampEnabled = getIsLearningGoalsRevampEnabled();
      if (isLearningGoalsRevampEnabled) {
        dispatch(
          updateAIConversation({
            input: {
              id: aiConversationId,
              metadata: {
                ...conversationMetadata,
                allFields: updatedConversationFields,
              },
            },
          })
        );
        return;
      }
      const fieldValue = _.get(params, fieldUID, []);
      const conversationAllFields = _.get(
        conversationMetadata,
        "allFields",
        []
      );

      let newConversationAllFields = [...conversationAllFields];

      // * remove field if value empty
      if (_.isEmpty(fieldValue)) {
        newConversationAllFields = _.filter(
          newConversationAllFields,
          field => !_.isEqual(_.get(field, "uid"), fieldUID)
        );
      }
      // * update or add new field
      else {
        const fieldData = _.find(newConversationAllFields, { uid: fieldUID });

        const resolvedMinimalTree = getResolvedOptimisticValue({
          resolvedValue: _.get(fieldData, "resolvedMinimalTree", {}),
          fieldUID,
          fieldTemplateObj: _.get(fieldList, fieldUID, {}),
          params: extraParams,
          value: fieldValue,
        });

        const newOrUpdatedFieldData = {
          id: _.get(fieldData, "id", generateRandomId()),
          uid: fieldUID,
          value: fieldValue,
          resolvedMinimalTree,
        };

        // * add new field
        if (_.isEmpty(fieldData)) {
          newConversationAllFields.push(newOrUpdatedFieldData);
        }
        // * update field
        else {
          const fieldIndex = _.findIndex(newConversationAllFields, {
            uid: fieldUID,
          });
          newConversationAllFields[fieldIndex] = newOrUpdatedFieldData;
        }
      }

      dispatch(
        updateAIConversation({
          input: {
            id: aiConversationId,
            metadata: {
              ...conversationMetadata,
              allFields: newConversationAllFields,
            },
          },
        })
      );
    } catch (err) {
      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
    }
  };
};

// * to update conversation's localFields
export const updateLocalFields = ({
  aiConversationId,
  context,
  conversationMetadata,
}) => {
  return async dispatch => {
    if (!_.isEmpty(context)) {
      try {
        const localFields = _.get(conversationMetadata, "localFields", []);
        const updatedLocalFields = [...localFields];

        // If any of the field update then only call updateAIConversation
        let shouldUpdate = false;

        _.forEach(context, (value, key) => {
          // Get the existing data at the specified index
          const existingField = _.find(updatedLocalFields, { field: key });

          switch (key) {
            case CF_MYP_CONTENT:
              {
                const isDifferentValue =
                  !_.isEmpty(existingField) &&
                  !_.isEqual(_.get(existingField, "value"), value);

                // Update the value at the specified index
                if (isDifferentValue) {
                  const fieldIndex = _.findIndex(updatedLocalFields, {
                    field: key,
                  });

                  _.set(updatedLocalFields, fieldIndex, {
                    ...existingField,
                    value,
                  });

                  shouldUpdate = true;
                }
                // If the index doesn't exist, push a new object into the array
                else if (_.isEmpty(existingField)) {
                  updatedLocalFields.push({
                    field: key,
                    value,
                  });

                  shouldUpdate = true;
                }
              }
              break;
          }
        });

        if (shouldUpdate) {
          dispatch(
            updateAIConversation({
              input: {
                id: aiConversationId,
                metadata: {
                  ...conversationMetadata,
                  localFields: updatedLocalFields,
                },
              },
            })
          );
        }
      } catch (err) {
        dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      }
    }
  };
};
